import { Fragment, useCallback } from "react";
import { Pressable, StyleSheet, View } from "@-/com.npmjs.react-native";
import { useAnimatedStyle } from "@-/com.npmjs.react-native-reanimated";
import { useSafeAreaInsets } from "@-/com.npmjs.react-native-safe-area-context";
import { NativeOnlyAnimatedView } from "@-/dev.improve.haniverse.components.ui.native-only-animated-view";
import {
  getPrimaryNavigationBarBottomClearance,
  getPrimaryNavigationBarRowHeight,
} from "@-/dev.improve.haniverse.lib.primary-navigation-strip";
import { NavContext } from "@-/dev.improve.haniverse.routes.nav._context";
import { Text } from "@-/dev.improve.haniverse.components.ui.text";
import { useAppSelectionActions } from "@-/dev.improve.haniverse.routes.nav.app._lib.morpheme.tile.selection";
import { useContextSelectors } from "@-/com.npmjs.use-context-selectors";
import { MorphemeSelectionOverlayActionButton } from "#morpheme-selection-overlay-action-button";
import { useMorphemeSelectionOverlayActions } from "#use-morpheme-selection-overlay-actions";
import type { MorphemeSelectionOverlayContentGate } from "#use-morpheme-selection-overlay-content-gate";

// How far the bar's opaque surface extends *below* the screen's bottom edge. Nothing is drawn
// there in the resting state — it exists purely so the bar can never be the source of a gap at
// the bottom either.
const BOTTOM_OVERSHOOT_PX = 64;

const styles = StyleSheet.create({
  // Pinned to the bottom edge across all app screens; `box-none` lets touches pass through the
  // wrapper everywhere except the bar itself — so the morpheme-view toolbar (Levels / Characters /
  // Primitives / Words) sitting just above the tab bar stays both visible and tappable.
  wrapper: {
    bottom: 0,
    left: 0,
    position: "absolute",
    right: 0,
    zIndex: 50,
  },
  // The opaque surface runs from the bar's top hairline all the way past the bottom edge. The
  // contents are laid out in the top `rowHeight + bottomClearance`; the overshoot below is bare
  // background. `overflow: "visible"` is implicit — nothing clips it.
  surface: {
    marginBottom: -BOTTOM_OVERSHOOT_PX,
    paddingBottom: BOTTOM_OVERSHOOT_PX,
  },
});

export function MorphemeSelectionOverlayContent({
  gate,
}: {
  gate: MorphemeSelectionOverlayContentGate;
}) {
  const insets = useSafeAreaInsets();
  const [androidUsesButtonNavBar] = useContextSelectors(
    NavContext,
    (c) => c.androidUsesButtonNavBar,
  );
  const { stopSelecting } = useAppSelectionActions();

  const { actions, isRunningAction, runAndDismiss, selectedCount } =
    useMorphemeSelectionOverlayActions();

  const handleActionPress = useCallback(
    (actionKey: string) => {
      const action = actions.find((candidate) => candidate.key === actionKey);
      if (action === undefined) {
        return;
      }
      runAndDismiss(action.run);
    },
    [actions, runAndDismiss],
  );

  // Match the primary navigation strip's footprint exactly so the action bar replaces *only* the tab bar and
  // its top edge lines up with the tab bar's top: the icon/label row (`rowHeight`) plus the
  // home-indicator clearance below it. Everything above the bar (the morpheme-view toolbar) is
  // left uncovered. Don't grow past this — it should read as the tab bar.
  const rowHeight = getPrimaryNavigationBarRowHeight();
  const bottomClearance = getPrimaryNavigationBarBottomClearance(insets.bottom, {
    androidUsesButtonNavBar,
  });
  const barHeight = rowHeight + bottomClearance;

  const { hiddenFraction } = gate;
  const slideHeight = barHeight + BOTTOM_OVERSHOOT_PX;
  // A fraction (not points), so a height change mid-animation — e.g. a safe-area inset arriving
  // late — cannot strand the bar at a stale pixel offset.
  const animatedStyle = useAnimatedStyle(() => ({
    transform: [{ translateY: hiddenFraction.get() * slideHeight }],
  }));

  return (
    <View pointerEvents="box-none" style={styles.wrapper}>
      <NativeOnlyAnimatedView style={animatedStyle}>
        <View
          className="border-t border-border bg-background px-2"
          style={[styles.surface, { height: barHeight + BOTTOM_OVERSHOOT_PX }]}
        >
          {/* Action buttons on top: a compact single row of icon-left-of-text buttons. The vertical
              hairlines between them run from the top edge down; a horizontal hairline closes off
              the row below. */}
          <View className="h-7 flex-row">
            {actions.map((action, index) => (
              <Fragment key={action.key}>
                {index > 0 && <View className="w-px bg-border" />}
                <MorphemeSelectionOverlayActionButton
                  actionKey={action.key}
                  disabled={selectedCount === 0 || isRunningAction || action.disabled}
                  icon={action.icon}
                  label={action.label}
                  onPress={handleActionPress}
                />
              </Fragment>
            ))}
          </View>
          <View className="h-px bg-border" />
          {/* Footer: a single pressable that clears the selection — "Deselect All" with a tiny
              "N selected" beneath it, centered, filling the space down to the device bottom.
              Sized explicitly rather than `flex-1` so it fills only the real bar, not the
              below-the-fold overshoot. */}
          <Pressable
            // Include the running count in the button's own label: this Pressable
            // is the accessibility node iOS exposes for the footer, and it absorbs
            // its child Texts' labels — so a nested `${selectedCount} selected`
            // Text is NOT independently matchable (by VoiceOver or by Maestro's
            // `tapOn`/`assertVisible "N selected"`). Folding the count in keeps the
            // selection count screen-reader-announced AND test-assertable.
            accessibilityLabel={`${selectedCount} selected, Deselect all`}
            accessibilityRole="button"
            className="items-center justify-center rounded-xs active:bg-accent"
            hitSlop={6}
            onPress={stopSelecting}
            // 28px action row + 1px hairline above.
            // The bar itself deliberately spans the gesture / button-navigation clearance so its
            // background replaces the tab bar all the way to the screen edge. Keep the two-line
            // footer content above that clearance, though; otherwise the selected-count line sits
            // behind Android's gesture handle and is visibly clipped.
            style={{ height: Math.max(0, barHeight - 29), paddingBottom: bottomClearance }}
          >
            <Text className="text-sm font-normal text-destructive/80">Deselect All</Text>
            <Text className="text-[9px] font-bold text-foreground">{selectedCount} selected</Text>
          </Pressable>
        </View>
      </NativeOnlyAnimatedView>
    </View>
  );
}
